import { useRef, useLayoutEffect } from 'react';

import type { PublicPluginAPI } from '@atlaskit/editor-common/types';
import {
	type Mode,
	type ConnectivityPlugin,
	isOfflineMode,
} from '@atlaskit/editor-plugin-connectivity';
import type { AnalyticsPlugin } from '@atlaskit/editor-plugin-analytics';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import { sendableSteps } from '@atlaskit/prosemirror-collab';

import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';

import { resyncOnline } from '../../pm-plugins/resync-online.ts';
import { editorOfflineStoragePluginKey } from '../../pm-plugins/main.ts';
import { syncWithOfflineStorageUnthrottled } from '../../pm-plugins/syncWithOfflineStorage.ts';
import type { StepsDatabase } from '../../stepsDatabase.ts';

import { useCreateOfflineVersion } from './useCreateOfflineVersion.ts';

/**
 * `usePluginStateEffect` has a debounce - we avoid that here to ensure we
 * catch all states on a critical piece of code
 *
 * @param api
 * @param cb
 */
const useConnectivityEffect = (
	api: PublicPluginAPI<[ConnectivityPlugin]> | undefined,
	cb: (props: { prevMode: Mode | undefined; nextMode: Mode | undefined }) => void,
) => {
	// Get the latest effect similar to `usePluginStateEffect` (no debounce)
	const latestEffect = useRef<typeof cb | undefined>(cb);
	useLayoutEffect(() => {
		latestEffect.current = cb;
		return () => {
			latestEffect.current = undefined;
		};
	}, [cb]);

	useLayoutEffect(() => {
		return api?.connectivity?.sharedState.onChange(({ prevSharedState, nextSharedState }) => {
			latestEffect.current?.({ prevMode: prevSharedState?.mode, nextMode: nextSharedState?.mode });
		});
	}, [api]);
};

/**
 * Resync the editor online
 */
export const useResyncOnline = ({
	api,
	editorView,
	stepsDatabase,
	broadcastUpdate,
	contentId,
	flags,
}: {
	api: PublicPluginAPI<[AnalyticsPlugin, ConnectivityPlugin]> | undefined;
	editorView: EditorView;
	stepsDatabase: StepsDatabase;
	broadcastUpdate: () => void;
	contentId: string | undefined;
	flags: FlagsStateContainer | null;
}) => {
	const { createVersion: createOfflineVersion } = useCreateOfflineVersion({
		contentId,
		api,
		stepsDatabase,
		flags,
	});
	const isActivelySyncing = useRef(false);

	useConnectivityEffect(api, ({ prevMode, nextMode }) => {
		const { status, disabled } = editorOfflineStoragePluginKey.getState(editorView.state) ?? {};
		if (status !== 'ready' || disabled) {
			return;
		}

		// Only allow one sync at a time. This is to avoid flickering of the connection status causing online and offline resyncs to run at the same time.
		if (isActivelySyncing.current) {
			return;
		}
		isActivelySyncing.current = true;

		if (isOfflineMode(prevMode) && nextMode === 'online') {
			// Try to prioritise the "active" tab by delaying the inactive tabs, this is to minimise
			// our chance of disrupting the user by the reset
			if (document && !document?.hasFocus?.()) {
				// Give the active document ~1s to connect
				setTimeout(
					() =>
						resyncOnline(api, stepsDatabase, editorView, false, {
							createOfflineVersion,
						}).finally(() => {
							isActivelySyncing.current = false;
						}),
					document?.visibilityState === 'visible' ? 500 : 1000,
				);
			} else {
				void resyncOnline(api, stepsDatabase, editorView, true, {
					createOfflineVersion,
				}).finally(() => {
					isActivelySyncing.current = false;
				});
			}
		} else if (isOfflineMode(nextMode)) {
			void stepsDatabase.validateIndexedDBAccess().then((isIndexedDBAvailable) => {
				if (isIndexedDBAvailable) {
					void stepsDatabase
						.clear(true)
						.then(async (cleared) => {
							if (cleared) {
								await stepsDatabase.releaseSyncLock?.();
							}
						})
						.finally(() => {
							// Resync the db with the current state of the unconfirmed steps
							const { version, steps } = sendableSteps(editorView.state) ?? {};
							if (version !== undefined && steps !== undefined) {
								void syncWithOfflineStorageUnthrottled({
									editorView,
									stepsDatabase,
									version,
									steps,
									broadcast: broadcastUpdate,
								}).finally(() => {
									isActivelySyncing.current = false;
								});
							} else {
								isActivelySyncing.current = false;
							}
						});
				} else {
					isActivelySyncing.current = false;
				}
			});
		}
	});
};
